import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUniqueStates, getDistrictsForState } from '../../data/stateDistrictData';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ProfileQuickForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { firebaseUser, user, completeProfile, markProfileCompletedLocally, logout } = useAuth();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    mobile: firebaseUser?.phoneNumber || location.state?.phone || '',
    state: '',
    district: '',
    city: '',
    locality: '',
    age: '',
    pinCode: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load data from Firestore when user data is available
  useEffect(() => {
    if (user) {
      // Split name into firstName and lastName
      const nameParts = user.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setForm(prev => {
        // Only update if field is empty (to preserve user edits)
        return {
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
          state: prev.state || user.state || '',
          district: prev.district || user.district || '',
          city: prev.city || user.city || '',
          locality: prev.locality || user.address || '',
          age: prev.age || (user.age ? String(user.age) : ''),
          pinCode: prev.pinCode || user.pinCode || '',
          mobile: prev.mobile || firebaseUser?.phoneNumber || location.state?.phone || user.phone || ''
        };
      });
    }
  }, [user, firebaseUser, location.state?.phone]);

  const handle = (k: string, v: string) => {
    setForm(p => {
      const newForm = { ...p, [k]: v };
      // Reset district when state changes
      if (k === 'state') {
        newForm.district = '';
      }
      return newForm;
    });
  };

  const states = getUniqueStates();
  const districts = form.state ? getDistrictsForState(form.state) : [];

  const onSubmit = async () => {
    if (!firebaseUser) return;
    if (!form.firstName.trim() || !form.lastName.trim() || !form.state || !form.district || !form.city.trim() || !form.locality.trim() || !form.age || form.pinCode.length !== 6) {
      setError('Please fill all fields correctly.');
      return;
    }
    setSaving(true);
    try {
      // Optimistic unlock so UI hides immediately
      markProfileCompletedLocally();
      // Use the completeProfile function from useAuth to properly update profile
      await completeProfile({
        name: `${form.firstName} ${form.lastName}`,
        phone: firebaseUser.phoneNumber || location.state?.phone || '',
        upiId: '',
        address: form.locality,
        city: form.city,
        state: form.state,
        district: form.district,
        pinCode: form.pinCode,
        accountType: 'Painter',
        age: Number(form.age),
        photoFile: null
      });
      // Continue onboarding: go to KYC step
      navigate('/onboarding/kyc', { replace: true });
    } catch (e: any) {
      // If saving fails, revert lock and show error
      setError(e.message || 'Failed to save');
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel registration? You will be logged out.')) {
      await logout();
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#ee4238] to-[#af201c]">
      {/* Header like Login Page */}
      <div className="px-6 pt-10 pb-6 text-center sticky top-0 z-50 bg-gradient-to-b from-[#ee4238] to-[#af201c] shadow-md">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleCancel}
            className="text-white/90 hover:text-white transition-colors flex items-center gap-1 sm:gap-2 px-2 sm:px-0 py-1 sm:py-0"
          >
            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm font-medium">Cancel</span>
          </button>
          <div className="flex-1"></div>
        </div>
        <h1 className="text-3xl font-black text-white">Complete your profile</h1>
        <p className="text-white/90 mt-5 text-sm leading-relaxed">
          This mobile number is not registered with us. Please complete the registration form to join our platform.
        </p>
        
        {/* Progress Steps */}
        <div className="mt-6 flex justify-center items-center space-x-4">
          {/* Step 1 - Personal (Active) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-[#ee4238] rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">1</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">Personal</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white/30"></div>
          
          {/* Step 2 - KYC */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
              <span className="text-white/60 text-xs font-bold">2</span>
            </div>
            <span className="text-white/60 text-xs font-medium mt-1">KYC</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white/30"></div>
          
          {/* Step 3 - Bank */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
              <span className="text-white/60 text-xs font-bold">3</span>
            </div>
            <span className="text-white/60 text-xs font-medium mt-1">Bank</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 w-full bg-white/20 rounded-full h-1">
          <div className="bg-white h-1 rounded-full" style={{ width: '33%' }}></div>
        </div>
      </div>

      {/* Body Card */}
      <div className="flex-1 bg-white rounded-t-3xl p-6 pb-28">
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}

        <div className="space-y-4">
          {/* Account Type - Fixed */}
          <div>
            <label className="block text-[#111827] text-sm font-medium mb-2">Account Type</label>
            <div className="w-full rounded-2xl border border-gray-300 bg-gray-100 px-4 py-3 text-gray-600">
              Painter
            </div>
          </div>

          {/* First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">First Name *</label>
              <input value={form.firstName} onChange={e => handle('firstName', e.target.value)} placeholder="Enter first name" className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">Last Name *</label>
              <input value={form.lastName} onChange={e => handle('lastName', e.target.value)} placeholder="Enter last name" className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
            </div>
          </div>

          {/* Mobile removed from UI as requested; still captured from auth context */}

          {/* State & District */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">State *</label>
              <select value={form.state} onChange={e => handle('state', e.target.value)} className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent">
                <option value="">Select State</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">District *</label>
              <select value={form.district} onChange={e => handle('district', e.target.value)} disabled={!form.state} className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400">
                <option value="">Select District</option>
                {districts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {/* City & Locality */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">City *</label>
              <input value={form.city} onChange={e => handle('city', e.target.value)} placeholder="Enter city" className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-[#111827] text-sm font-medium mb-2">Locality *</label>
              <input value={form.locality} onChange={e => handle('locality', e.target.value)} placeholder="Area / Locality" className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
            </div>
          </div>

          {/* PIN Code */}
          <div>
            <label className="block text-[#111827] text-sm font-medium mb-2">PIN Code *</label>
            <input type="tel" inputMode="numeric" pattern="[0-9]*" value={form.pinCode} onChange={e => handle('pinCode', e.target.value.replace(/\D/g, '').slice(0,6))} placeholder="6-digit PIN" maxLength={6} className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
          </div>

          {/* Age */}
          <div>
            <label className="block text-[#111827] text-sm font-medium mb-2">Age *</label>
            <input type="tel" inputMode="numeric" pattern="[0-9]*" value={form.age} onChange={e => handle('age', e.target.value.replace(/\D/g, '').slice(0,2))} placeholder="Enter your age" maxLength={2} className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent" />
          </div>

          {/* Spacer above fixed button */}
          <div className="h-4"></div>
        </div>
      </div>
      {/* Fixed bottom action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-6 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <button
          onClick={onSubmit}
          disabled={saving || !form.firstName.trim() || !form.lastName.trim() || !form.state || !form.district || !form.city.trim() || !form.locality.trim() || !form.age || form.pinCode.length !== 6}
          className="w-full bg-gradient-to-r from-[#ee4238] to-[#d63a32] text-white py-4 px-6 font-extrabold text-lg rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
};

export default ProfileQuickForm;


